import { FC, useEffect, useReducer, useRef } from 'react'
import { useTranslation } from 'next-i18next'
import { useForm } from 'react-hook-form'
import type {
    GiftCardProduct as GiftCardProductInterface,
    CustomizableOptionInterface
} from '@pylot-data/pylotschema'
import { ProductStockStatus } from '@pylot-data/enums/ProductStockStatus.d'
import { Button } from '@corratech/pylot-ui'
import { AmountDropdown, OtherAmountEnum } from './AmountDropdown'
import { OpenAmount } from './OpenAmount'
import { useAddToCart } from '@components/alfakher/pylot-cart-manager'
import { getMapFieldWithUid } from './getMapFieldWithUid'
import formStyles from '@commonStyles/Forms.module.scss'
import cn from 'classnames'
import s from '@pagestyles/Giftcard.module.scss'
import { SET_MESSAGE, SET_TARGET, selectedAmountReducer } from './reducer'
import BuyNow from '../../../stores/ooka_default/components/icons/BuyNow'
import { formatPrice } from '@corratech/pylot-price'
import { useRouter } from 'next/router'
import { ButtonLabel } from '@pylot-data/hooks/product/utils/pdpProductLabels'

export interface GiftCardProductInputArgs {
    sender_name: string
    sender_email: string
    receiver_name: string
    receiver_email: string
    gift_message: string
    gift_amounts: string | OtherAmountEnum.Other
    gift_open_amounts: number
}

export type Props = {
    product: GiftCardProductInterface
}

export type GiftProductOptionsValueUid = {
    value: { uid: string }
} & CustomizableOptionInterface

export const GiftCardProduct: FC<Props> = ({ product }) => {
    const { t } = useTranslation(['common', 'pdp'])
    const { addToCart, isAdding } = useAddToCart()
    const {
        stock_status,
        allow_message,
        message_max_length,
        gift_card_options,
        giftcard_amounts,
        allow_open_amount,
        price_range
    } = product

    const {
        reset,
        register,
        unregister,
        control,
        handleSubmit,
        clearErrors,
        setError,
        formState: { errors }
    } = useForm<GiftCardProductInputArgs>({
        mode: 'onChange'
    })
    const { locale = 'en' } = useRouter()

    const initialState = {
        message: '',
        target: ''
    }
    const isOutOfStock = stock_status === ProductStockStatus.OutOfStock
    const [state, dispatch] = useReducer(selectedAmountReducer, initialState)
    const addTocartRef = useRef<HTMLDivElement>(null)
    const getButtonLabel = () => {
        if (isAdding) return t('pdp||giftCard|Adding...')
        return t(`pdp||giftCard|${ButtonLabel.ADD_TO_CART}`)
    }

    const triggerErrorPriceInput = () =>
        dispatch({
            type: SET_MESSAGE,
            payload: t(`From {{min}} to {{max}}`, {
                min: formatPrice(price_range.minimum_price.final_price, locale),
                max: formatPrice(price_range.maximum_price!.final_price, locale)
            })
        })

    useEffect(() => {
        if (Number(giftcard_amounts?.length) > 0) {
            triggerErrorPriceInput()
        }
        dispatch({
            type: SET_TARGET,
            payload: Number(giftcard_amounts?.length) ? 'dropdown' : 'other'
        })
    }, [])

    const onSubmit = async (formInput: GiftCardProductInputArgs) => {
        triggerErrorPriceInput()
        const hasSelectedAmount =
            formInput.gift_amounts &&
            formInput.gift_amounts !== OtherAmountEnum.Other

        await addToCart(
            [
                {
                    sku: product.sku!,
                    entered_options: getMapFieldWithUid({
                        giftCardOptions: gift_card_options as GiftProductOptionsValueUid[],
                        formInput
                    }),
                    ...(hasSelectedAmount && {
                        selected_options: [formInput.gift_amounts]
                    }),
                    quantity: 1
                }
            ],
            [product]
        )

        reset({
            ...(giftcard_amounts?.length &&
                allow_open_amount && {
                    gift_amounts: ''
                })
        })
    }

    const scrollToTop = () => {
        return new Promise((resolve) => {
            addTocartRef?.current?.scrollIntoView({
                block: 'start',
                behavior: 'smooth'
            })
            setTimeout(resolve, 600)
        })
    }

    const handleGiftCardSubmit = async () => {
        await scrollToTop()
        await handleSubmit(onSubmit)()
    }

    return (
        <div ref={addTocartRef} className={s['selector-with-add-to-cart']}>
            <p className="mb-4 text-2xl md:text-3.5xl font-normal leading-8">
                {state.message}
            </p>
            <div className={cn(s['gift-card-container'], formStyles.root)}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <AmountDropdown
                        register={register}
                        control={control}
                        product={product}
                        dispatch={dispatch}
                        unregister={unregister}
                        price_range={price_range}
                        errors={errors}
                        clearErrors={clearErrors}
                        setError={setError}
                    />
                    {state.target === OtherAmountEnum.Other &&
                        product.allow_open_amount && (
                            <OpenAmount
                                control={control}
                                register={register}
                                product={product}
                                errors={errors}
                                unregister={unregister}
                                dispatch={dispatch}
                                price_range={price_range}
                            />
                        )}
                    <div className="form-grid">
                        <div className="form-group">
                            <label className="heading" htmlFor="sender_name">
                                {t('pdp||giftCard|Sender Name')}
                                <span className="asterisk">*</span>
                            </label>
                            <input
                                type="text"
                                {...register('sender_name', { required: true })}
                                className={cn('form-input', {
                                    'border-alert-danger': errors.sender_name
                                })}
                                id="sender_name"
                            />
                            {errors.sender_name && (
                                <span className="error">
                                    {t('pdp||giftCard|This is required field')}
                                </span>
                            )}
                        </div>
                        <div className="form-group">
                            <label className="heading" htmlFor="sender_email">
                                {t('pdp||giftCard|Sender Email')}
                                <span className="asterisk">*</span>
                            </label>
                            <input
                                type="text"
                                {...register('sender_email', {
                                    required: 'This field is required',
                                    pattern: {
                                        value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/,
                                        message:
                                            'Please enter a valid email, such as example@example.com'
                                    }
                                })}
                                className={cn('form-input', {
                                    'border-alert-danger': errors.sender_email
                                })}
                                id="sender_email"
                            />
                            {errors.sender_email && (
                                <span className="error">
                                    {t(
                                        `pdp||giftCard|${errors.sender_email.message}`
                                    )}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="form-grid">
                        <div className="form-group">
                            <label className="heading" htmlFor="receiver_name">
                                {t('pdp||giftCard|Receiver Name')}
                                <span className="asterisk">*</span>
                            </label>
                            <input
                                type="text"
                                {...register('receiver_name', {
                                    required: true
                                })}
                                className={cn('form-input', {
                                    'border-alert-danger': errors.receiver_name
                                })}
                                id="receiver_name"
                            />
                            {errors.receiver_name && (
                                <span className="error">
                                    {t('pdp||giftCard|This is required field')}
                                </span>
                            )}
                        </div>
                        <div className="form-group">
                            <label className="heading" htmlFor="receiver_email">
                                {t('pdp||giftCard|Receiver Email')}
                                <span className="asterisk">*</span>
                            </label>
                            <input
                                type="text"
                                {...register('receiver_email', {
                                    required: 'This field is required',
                                    pattern: {
                                        value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/,
                                        message:
                                            'Please enter a valid email, such as example@example.com'
                                    }
                                })}
                                className={cn('form-input', {
                                    'border-alert-danger': errors.receiver_email
                                })}
                                id="receiver_email"
                            />
                            {errors.receiver_email && (
                                <span className="error">
                                    {t(
                                        `pdp||giftCard|${errors.receiver_email.message}`
                                    )}
                                </span>
                            )}
                        </div>
                    </div>
                    {allow_message && (
                        <div className="form-group">
                            <label className="heading" htmlFor="gift_message">
                                {t('pdp||giftCard|Message')}
                            </label>
                            <textarea
                                {...register('gift_message', {
                                    maxLength: message_max_length!
                                })}
                                className={cn('form-input')}
                                id="gift_message"
                            />
                            {errors.gift_message && (
                                <span className="error">
                                    {t(
                                        'pdp||giftCard|Maximum length of the message is {{ length }} characters.',
                                        {
                                            length: message_max_length || 255
                                        }
                                    )}
                                </span>
                            )}
                        </div>
                    )}
                    <Button
                        loading={isAdding}
                        disabled={isAdding || isOutOfStock}
                        className={cn(
                            s['giftcard-add-to-cart'],
                            'addToCart_Gift'
                        )}
                        onClick={handleGiftCardSubmit}
                        type="button"
                        variant="primary"
                    >
                        <BuyNow />
                        {isOutOfStock
                            ? t('pdp||giftCard|Out of stock')
                            : getButtonLabel()}
                    </Button>
                </form>
            </div>
        </div>
    )
}
